// Icons
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
// MUI
import {
  Box,
  InputAdornment,
  ListItemIcon,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import Fuse from 'fuse.js';
import {useFlags} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';

import {reset as orgReset} from '../../data/session';
import {clearSharedDocumentData} from '../../util/sharedDocumentStorage';

const MIN_ORG_COUNT_FOR_SEARCH = 10;

export const OrgSwitcher = ({onClose}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const flags = useFlags();
  const {currentOrg, orgs} = useSelector((state) => state.session);
  const orgsWithoutParent = useMemo(
    () => orgs.filter((org) => !org.orgMetadata?.isParentOrganization),
    [orgs]
  );
  const [searchTerm, setSearchTerm] = useState('');

  // Fuse search for orgs
  const fuse = useMemo(
    () =>
      new Fuse(orgsWithoutParent, {
        keys: ['orgName'],
        threshold: 0.4,
      }),
    [orgsWithoutParent]
  );

  // Filter orgs based on search term (memoized to avoid re-running Fuse search on every render)
  const filteredOrgs = useMemo(
    () =>
      searchTerm.trim() === ''
        ? orgsWithoutParent
        : fuse.search(searchTerm).map((result) => result.item),
    [searchTerm, fuse, orgsWithoutParent]
  );

  // Only show search if there are more than 10 orgs
  const shouldShowSearch = orgsWithoutParent.length > MIN_ORG_COUNT_FOR_SEARCH;

  const handleSetOrg = useCallback(
    (org) => {
      onClose();
      localStorage.setItem('selectedOrgId', org.orgId);
      clearSharedDocumentData();
      dispatch(orgReset());

      if (location.pathname.match(/^\/documents\/[a-zA-Z0-9]+$/)) {
        if (flags.documentFolders) {
          window.location.href = '/folders';
        } else {
          window.location.href = '/documents';
        }
      } else if (
        location.pathname.match(/^\/folders\/[a-zA-Z0-9]+\/documents$/)
      ) {
        window.location.href = '/folders';
      } else {
        window.location.reload();
      }
    },
    [onClose, dispatch, flags.documentFolders, location.pathname]
  );

  return (
    <Box>
      <MenuItem
        disableRipple
        onClick={(e) => e.preventDefault()}
        sx={{
          '&:hover': {
            backgroundColor: 'transparent',
            cursor: 'default',
          },
          pointerEvents: 'none',
        }}
      >
        <Typography color="#5F8882" fontSize={10} fontWeight={600}>
          USE DASHBOARD AS...
        </Typography>
      </MenuItem>
      {shouldShowSearch && (
        <Box onClick={(e) => e.stopPropagation()} sx={{px: 2, py: 1}}>
          <TextField
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{color: '#6B7280', height: 16, width: 16}} />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Search..."
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  border: '1px solid #5F8882',
                },
                '&:hover fieldset': {
                  border: '1px solid #D1D5DB',
                },
                '& fieldset': {
                  border: '1px solid #E5E7EB',
                },
                '& input': {
                  padding: '6px 8px',
                },
                backgroundColor: '#fff',
                fontSize: '14px',
                height: '32px',
              },
              width: '100%',
            }}
            value={searchTerm}
          />
        </Box>
      )}

      {/* Scrollable Org List */}
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
          },
          maxHeight: '320px',
          overflowY: 'auto',
        }}
      >
        {filteredOrgs.length === 0 ? (
          <MenuItem disabled sx={{height: 32}}>
            <Typography color="#9CA3AF" fontSize={14} fontWeight={400}>
              No organizations found
            </Typography>
          </MenuItem>
        ) : (
          filteredOrgs.map((org, index) => (
            <MenuItem
              key={index}
              onClick={() => handleSetOrg(org)}
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: 32,
                justifyContent: 'space-between',
                overflow: 'hidden',
              }}
            >
              <Typography
                color="#052E28"
                fontWeight={500}
                fontSize={'0.9375rem'}
                sx={{overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0}}
              >
                {org.orgName}
              </Typography>

              {currentOrg?.orgId === org.orgId && (
                <ListItemIcon sx={{marginLeft: 'auto', minWidth: 'auto'}}>
                  <CheckIcon sx={{color: '#0F1E24', height: 16, width: 16}} />
                </ListItemIcon>
              )}
            </MenuItem>
          ))
        )}
      </Box>
    </Box>
  );
};

OrgSwitcher.propTypes = {
  onClose: PropTypes.func.isRequired,
};
